import { QueryClient, useQuery } from '@tanstack/react-query';
import { ReactElement } from 'react';

import { http } from 'api/client';
import { deserialize, parseDateTime } from 'api/deserialize';
import { User } from 'api/endpoints/users/types';

import { assert } from 'utils/assert';
import { MINUTE_IN_MILLIS } from 'utils/date';

import { Loading } from 'components/loading';

export const CURRENT_USER_QUERY_KEY = ['currentUser'];

export function CurrentUserProvider({ children }: { children: ReactElement }) {
  // Fetch on AuthenticatedApp mount
  // Refetch on window focus after 5 minutes of stale data
  const { isLoading, data: user } = useQuery({
    queryKey: CURRENT_USER_QUERY_KEY,
    queryFn: () => fetchCurrentUser(),
    refetchOnWindowFocus: true,
    staleTime: 5 * MINUTE_IN_MILLIS,
    onError: () => {},
  });

  if (isLoading || !user) {
    return <Loading />;
  }

  return children;
}

export function useCurrentUser({ refetchOnMount }: { refetchOnMount?: boolean | 'always' } = {}) {
  // Refetch on hook mount after 30 minutes of stale data
  const { data: user } = useQuery({
    queryKey: CURRENT_USER_QUERY_KEY,
    queryFn: () => fetchCurrentUser(),
    staleTime: 30 * MINUTE_IN_MILLIS,
    refetchOnMount,
  });

  assert(user, 'The hook `useCurrentUser` must be used within an `CurrentUserProvider`.');

  return user;
}

export function refetchCurrentUser(queryClient: QueryClient) {
  queryClient.invalidateQueries(CURRENT_USER_QUERY_KEY);
}

async function fetchCurrentUser() {
  const { data } = await http.get<{ user: User }>('current_user');

  return deserialize(data.user, {
    'updatedAt': parseDateTime,
    'createdAt': parseDateTime,
    'identifiers[].updatedAt': parseDateTime,
  });
}
